/**
 * @fileoverview Modal dialog for selecting a world configuration from available options.
 * @module ui/WorldSelectDialog
 */

import { DEBUG_CBM } from '../constants.js';
import { BaseDialog } from './BaseDialog.js';
import { sanitizeHtml } from './InformationDialog.js';
import { escapeHtml, getOwnValue } from '../utils/jrfuncs.js';


/**
 * Modal dialog for selecting a world data configuration.
 * @class
 * @extends BaseDialog
 */
export class WorldSelectDialog extends BaseDialog {

    /**
     * Creates a new WorldSelectDialog instance.
     * @param {Object} [options={}] - Configuration options
     * @param {string} [options.title='Select World Data'] - Modal title
     * @param {Object} [options.appConfig=null] - App configuration containing worlds list
     * @param {string} [options.currentWorldId=null] - ID of the currently loaded world
     */
    constructor(options = {}) {
        super({ idPrefix: 'worldSelectDialog' });

        this.options = {
            title: options.title || 'Select World Data',
            appConfig: options.appConfig || null,
            currentWorldId: options.currentWorldId || null
        };

        this.selectedWorldId = null;
    }


    /**
     * Returns the HTML for the world selection modal.
     * Displays a list of available worlds with badges for current and default.
     * @returns {string} Modal HTML string
     * @override
     */
    getModalHtml() {
        const worlds = this.options.appConfig?.worlds || {};
        const worldIds = Object.keys(worlds);
        const defaultWorldId = this.options.appConfig?.defaultWorldId || null;
        const currentWorldId = this.options.currentWorldId || null;

        let listItemsHtml = '';
        for (const worldId of worldIds) {
            const world = worlds[worldId];
            const isCurrent = worldId === currentWorldId;
            const isDefault = worldId === defaultWorldId;

            let badges = '';
            if (isCurrent) badges += '<span class="badge bg-primary ms-2">CURRENT</span>';
            if (isDefault) badges += '<span class="badge bg-secondary ms-1">DEFAULT</span>';

            const activeClass = isCurrent ? 'active' : '';
            const worldIdAttr = escapeHtml(worldId);
            listItemsHtml += `
                <a href="#" class="list-group-item list-group-item-action ${activeClass}"
                   data-world-id="${worldIdAttr}" id="${this.modalId}_item_${worldIdAttr}">
                    ${escapeHtml(world.label || worldId)}${badges}
                </a>
            `;
        }

        const bodyHtml = `
            <div class="world-select-dialog-content">
                <div class="row">
                    <div class="col-6">
                        <label class="form-label fw-bold">Available Worlds:</label>
                        <div class="list-group" id="${this.modalId}_worldList" style="max-height: 250px; overflow-y: auto;">
                            ${listItemsHtml}
                        </div>
                    </div>
                    <div class="col-6">
                        <label class="form-label fw-bold">Description:</label>
                        <div id="${this.modalId}_description" class="form-control bg-light" style="min-height: 150px; max-height: 250px; white-space: pre-wrap; overflow-y: auto;">
                            Select a world to see its description.
                        </div>
                    </div>
                </div>
            </div>
        `;

        const footerHtml = `
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" id="${this.modalId}_selectBtn" disabled>
                <i class="bi bi-check-lg"></i> Load World
            </button>
        `;

        return this.buildModalHtml({
            title: this.options.title,
            icon: 'bi-globe',
            bodyHtml,
            footerHtml,
            size: 'modal-lg'
        });
    }


    /**
     * Sets up event listeners for world list item clicks and the select button.
     * @override
     */
    setupEventListeners() {
        const worldList = this.getElement('_worldList');
        const selectBtn = this.getElement('_selectBtn');

        worldList.addEventListener('click', (e) => {
            const item = e.target.closest('.list-group-item');
            if (item) {
                e.preventDefault();
                this.selectWorldById(item.dataset.worldId);
            }
        });

        selectBtn.addEventListener('click', () => {
            if (this.selectedWorldId) {
                this.resolve(this.selectedWorldId);
            }
        });
    }


    /**
     * Called before the modal is shown.
     * Pre-selects the currently loaded world if one is set.
     * @override
     */
    onBeforeShow() {
        if (this.options.currentWorldId) {
            this.selectWorldById(this.options.currentWorldId);
        }
    }


    /**
     * Selects a world by ID, updating the active state in the list,
     * displaying its description, and enabling/disabling the select button.
     * @param {string} worldId - The ID of the world to select
     * @private
     */
    selectWorldById(worldId) {
        const worldList = this.getElement('_worldList');
        const descriptionDiv = this.getElement('_description');
        const selectBtn = this.getElement('_selectBtn');

        // Update active state
        worldList.querySelectorAll('.list-group-item').forEach(item => {
            item.classList.toggle('active', item.dataset.worldId === worldId);
        });

        this.selectedWorldId = worldId;

        const world = getOwnValue(this.options.appConfig?.worlds, worldId);
        if (world) {
            // the description may carry simple markup (links), so it goes through the same
            // allowlist sanitizer the world information dialog uses rather than raw innerHTML
            const descriptionHtml = sanitizeHtml(world.description || 'No description available.');
            const configPath = world.worldConfigPath || '--';
            const configPathHtml = `<br><br><span class="text-muted font-monospace" style="font-size: 0.85em;">Config path: ${escapeHtml(configPath)}</span>`;
            if (DEBUG_CBM) {
                descriptionDiv.innerHTML = descriptionHtml + configPathHtml;
            } else {
                descriptionDiv.innerHTML = descriptionHtml;
            }
            selectBtn.disabled = (worldId === this.options.currentWorldId);
        } else {
            descriptionDiv.textContent = 'World configuration not found.';
            selectBtn.disabled = true;
        }
    }


    /**
     * Shows the world select dialog (singleton - only one can be open at a time).
     * @param {Object} options - Configuration options
     * @returns {Promise<string|null>} Selected world ID, or null if cancelled
     */
    static async showWorldSelectDialog(options = {}) {
        return BaseDialog.showSingleton(WorldSelectDialog, options);
    }
}
